import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, FileText, Upload, Clock, CheckCircle, AlertTriangle, MessageSquare, Save, Play } from 'lucide-react';

interface Document {
  id: string;
  name: string;
  status: 'submitted' | 'pending';
  type: 'mandatory' | 'employee-provided';
  uploadedBy: 'employee' | 'manager';
  comments?: string;
}

const InformationProvided: React.FC = () => {
  const { ktId } = useParams();
  const navigate = useNavigate();
  
  const [documents, setDocuments] = useState<Document[]>([
    { id: '1', name: 'Process Documentation.pdf', status: 'submitted', type: 'mandatory', uploadedBy: 'employee' },
    { id: '2', name: 'Client Contact List.xlsx', status: 'pending', type: 'mandatory', uploadedBy: 'employee' },
    { id: '3', name: 'Monthly Reports Template.docx', status: 'submitted', type: 'mandatory', uploadedBy: 'employee' },
  ]);

  const [employeeTasks, setEmployeeTasks] = useState([
    { id: '1', name: 'Financial Analysis Procedures', path: '/shared/finance/procedures/', status: 'submitted' },
    { id: '2', name: 'Budget Planning Guidelines', path: '/shared/finance/budget/', status: 'pending' },
  ]);

  const [managerInputs, setManagerInputs] = useState({
    focusArea: '',
    jobDescription: '',
    projectDetails: ''
  });

  const [showDemo, setShowDemo] = useState(true);
  const [newDocument, setNewDocument] = useState<File | null>(null);

  const handleDocumentUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setNewDocument(file);
    }
  };

  const handleAddDocument = () => {
    if (newDocument) {
      const newDoc: Document = {
        id: Date.now().toString(),
        name: newDocument.name,
        status: 'submitted',
        type: 'employee-provided',
        uploadedBy: 'employee'
      };
      setDocuments([...documents, newDoc]);
      setNewDocument(null);
    }
  };

  const handleManagerInputChange = (field: string, value: string) => {
    setManagerInputs(prev => ({ ...prev, [field]: value }));
  };

  const handleSaveAndContinue = () => {
    navigate(`/document-access/${ktId}`);
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-4 mb-6">
          <div className="p-3 bg-blue-600 rounded-xl shadow-lg">
            <FileText className="w-8 h-8 text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Information Provided for KT</h1>
            <p className="text-gray-600 mt-1">Employee and Manager document upload and information gathering</p>
          </div>
        </div>
      </div>

      {/* Demo Video Section */}
      {showDemo && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-900">How the Process Works</h2>
            <button
              onClick={() => setShowDemo(false)}
              className="text-blue-600 hover:text-blue-700 text-sm font-medium"
            >
              Skip Demo
            </button>
          </div>
          <div className="bg-gray-100 rounded-lg p-8 text-center">
            <Play className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600 mb-4">Watch this 3-minute demo to understand the KT process</p>
            <button className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors">
              Play Demo Video
            </button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Employee Section */}
        <div className="space-y-6">
          <h2 className="text-2xl font-bold text-gray-900">Employee Tasks</h2>
          
          {/* Mandatory Documents */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Mandatory Documents</h3>
            <p className="text-gray-600 mb-4">Please upload the following important documents required for KT</p>
            
            <div className="space-y-3">
              {documents.filter(doc => doc.type === 'mandatory').map((doc) => (
                <div key={doc.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                  <div className="flex items-center gap-3">
                    <FileText className="w-5 h-5 text-gray-400" />
                    <span className="text-sm font-medium text-gray-900">{doc.name}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {doc.status === 'submitted' ? (
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-800 text-xs font-medium rounded-full">
                        <CheckCircle className="w-3 h-3" />
                        Submitted
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-yellow-100 text-yellow-800 text-xs font-medium rounded-full">
                        <Clock className="w-3 h-3" />
                        Pending
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Employee Task List */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Important Documents List</h3>
            <p className="text-gray-600 mb-4">Provide list and path of important documents</p>
            
            <div className="space-y-3 mb-4">
              {employeeTasks.map((task) => (
                <div key={task.id} className="p-4 border border-gray-200 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-gray-900">{task.name}</span>
                    {task.status === 'submitted' ? (
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    ) : (
                      <Clock className="w-5 h-5 text-yellow-600" />
                    )}
                  </div>
                  <p className="text-sm text-gray-600">Path: {task.path}</p>
                </div>
              ))}
            </div>
            
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-yellow-600 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-yellow-800">SLA Notice</p>
                  <p className="text-sm text-yellow-700 mt-1">
                    Please submit all required information within 3 business days. Delays may stall the KT process and trigger escalation to HR and Manager.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Additional Document Upload */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Additional Documents</h3>
            <p className="text-gray-600 mb-4">Upload any other relevant documents (local files, documents not in SharePoint/emails)</p>
            
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center mb-4">
              <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
              <div className="mb-2">
                <label htmlFor="additional-upload" className="cursor-pointer">
                  <span className="text-blue-600 hover:text-blue-700 font-medium">Click to upload</span>
                  <span className="text-gray-600"> or drag and drop</span>
                </label>
                <input
                  id="additional-upload"
                  type="file"
                  onChange={handleDocumentUpload}
                  className="hidden"
                />
              </div>
              <p className="text-xs text-gray-500">Max file size: 50MB</p>
            </div>
            
            {newDocument && (
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg mb-4">
                <span className="text-sm text-gray-900">{newDocument.name}</span>
                <button
                  onClick={handleAddDocument}
                  className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                >
                  Add Document
                </button>
              </div>
            )}
            
            {documents.filter(doc => doc.type === 'employee-provided').length > 0 && (
              <div className="space-y-2">
                <h4 className="font-medium text-gray-900">Uploaded Documents:</h4>
                {documents.filter(doc => doc.type === 'employee-provided').map((doc) => (
                  <div key={doc.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                    <FileText className="w-4 h-4 text-gray-400" />
                    <span className="text-sm text-gray-900">{doc.name}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Manager Section */}
        <div className="space-y-6">
          <h2 className="text-2xl font-bold text-gray-900">Manager Tasks</h2>
          
          {/* Manager Inputs */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">KT Focus Areas</h3>
            
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Focus Area for KT
                </label>
                <textarea
                  value={managerInputs.focusArea}
                  onChange={(e) => handleManagerInputChange('focusArea', e.target.value)}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Describe the key areas that should be the primary focus during knowledge transfer..."
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Job Description Details
                </label>
                <textarea
                  value={managerInputs.jobDescription}
                  onChange={(e) => handleManagerInputChange('jobDescription', e.target.value)}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Provide detailed job description and key responsibilities..."
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Important Projects & Other Details
                </label>
                <textarea
                  value={managerInputs.projectDetails}
                  onChange={(e) => handleManagerInputChange('projectDetails', e.target.value)}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="List important ongoing projects, stakeholder relationships, and other critical details..."
                />
              </div>
            </div>
          </div>

          {/* Manager Review Section */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Document Review & Feedback</h3>
            <p className="text-gray-600 mb-4">Review employee submissions and provide feedback</p>
            
            <div className="space-y-4">
              {documents.filter(doc => doc.uploadedBy === 'employee').map((doc) => (
                <div key={doc.id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <span className="font-medium text-gray-900">{doc.name}</span>
                    <div className="flex items-center gap-2">
                      <button className="text-blue-600 hover:text-blue-700 text-sm">
                        Review
                      </button>
                      <button className="text-green-600 hover:text-green-700 text-sm">
                        Approve
                      </button>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <MessageSquare className="w-4 h-4 text-gray-400 mt-1" />
                    <textarea
                      placeholder="Add comments or feedback for the employee..."
                      rows={2}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Save and Continue */}
      <div className="mt-8 flex justify-center">
        <button
          onClick={handleSaveAndContinue}
          className="inline-flex items-center gap-2 bg-blue-600 text-white px-8 py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors shadow-sm"
        >
          <Save className="w-5 h-5" />
          Save & Continue Later
        </button>
      </div>
    </div>
  );
};

export default InformationProvided;